import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Upload, Trash2, ImageIcon } from 'lucide-react';
import '../styles/ImageGallery.css';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

const getAuthToken = () => {
  return localStorage.getItem('admin_access_token') || localStorage.getItem('access_token');
};

export function ImageGallery({ isOpen, onClose, onSelect, currentImage }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchImages();
    }
  }, [isOpen]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/upload`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        
        // If there's a currentImage that's not in the uploaded list, add it
        if (currentImage) {
          const imageUrl = currentImage.startsWith('http') ? currentImage : `${API_URL}${currentImage}`;
          const imagePath = currentImage.startsWith('http') ? currentImage.replace(API_URL, '') : currentImage;
          const exists = data.some(img => img.url === imagePath || img.url === currentImage);
          
          if (!exists && imagePath) {
            // Extract filename from path
            const filename = imagePath.split('/').pop();
            data.unshift({
              url: imagePath,
              filename: filename,
              isExternal: currentImage.startsWith('http')
            });
          }
        }
        
        setImages(data);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only image files (JPG, PNG, GIF, WebP) are allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await fetch(`${API_URL}/api/upload/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: formData
      });

      const data = await res.json();
      if (data.success) {
        await fetchImages();
        onSelect(data.url);
        onClose();
      } else {
        alert(data.error || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) handleUpload(file);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handleUpload(e.dataTransfer.files[0]);
    }
  }, []);

  const handleDelete = async (e, filename) => {
    e.stopPropagation();
    
    if (deleteConfirm !== filename) {
      setDeleteConfirm(filename);
      setTimeout(() => setDeleteConfirm(null), 2000);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/upload/${filename}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (res.ok) {
        const deletedImage = images.find(img => img.filename === filename);
        if (deletedImage && currentImage === deletedImage.url) {
          onSelect('');
        }
        fetchImages();
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
    setDeleteConfirm(null);
  };

  const handleSelect = (image) => {
    onSelect(image.url);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="gallery-overlay" onClick={onClose}>
      <div className="gallery-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="gallery-header">
          <h3>Image Gallery</h3>
          <button className="gallery-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Compact Upload Area */}
        <div 
          className={`gallery-dropzone ${dragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !uploading && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
          
          {uploading ? (
            <div className="uploading-state">
              <div className="spinner" />
              <span>Uploading...</span>
            </div>
          ) : (
            <>
              <Upload size={20} className="dropzone-icon" />
              <span className="dropzone-text">
                {dragActive ? 'Drop to upload' : 'Drop image or click to browse'}
              </span>
            </>
          )}
        </div>

        {/* Image Grid */}
        <div className="gallery-content">
          {loading ? (
            <div className="center-state">
              <div className="spinner" />
            </div>
          ) : images.length === 0 ? (
            <div className="center-state">
              <ImageIcon size={32} className="empty-icon" />
              <span>No images yet</span>
            </div>
          ) : (
            <div className="gallery-grid">
              {images.map((image) => {
                const isSelected = currentImage === image.url || currentImage === `${API_URL}${image.url}`;
                const isConfirming = deleteConfirm === image.filename;
                const imageSrc = image.isExternal || image.url.startsWith('http') 
                  ? image.url 
                  : `${API_URL}${image.url}`;
                
                return (
                  <div
                    key={image.filename}
                    className={`gallery-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelect(image)}
                  >
                    <img 
                      src={imageSrc}
                      alt=""
                      crossOrigin="anonymous"
                      loading="lazy"
                    />
                    
                    {isSelected && (
                      <div className="selected-badge">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    )}
                    
                    <button
                      className={`gallery-delete-btn ${isConfirming ? 'confirm' : ''}`}
                      onClick={(e) => handleDelete(e, image.filename)}
                      title={isConfirming ? 'Click again to confirm' : 'Delete'}
                      aria-label={isConfirming ? 'Confirm delete image' : 'Delete image'}
                    >
                      <Trash2 size={14} strokeWidth={2.5} className="gallery-delete-icon" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImageGallery;
